import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { FeaturePermissionDto } from '../../api/models/feature-permission-dto';
import { TenantService } from './tenantService';
import { selectUserPermissions, selectUserRole, selectUserSupplierV1 } from '../state/selector';
import { SupplierListV1 } from 'src/app/api/models';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly store = inject(Store);
  private readonly tenantService = inject(TenantService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);
  constructor() {}

  async checkPermission(referenceID: string | null, feature: string, actions: string[]): Promise<boolean> {
    const userPermission = await firstValueFrom(this.store.select(selectUserPermissions));
    const userRole = await firstValueFrom(this.store.select(selectUserRole));

    const ownActions = actions.filter((action) => action.endsWith('Own'));
    const normalActions = actions.filter((action) => !action.endsWith('Own'));

    const hasOwnActionPermission = await this.hasAccessToOwnData(referenceID, userPermission, feature, ownActions);
    const hasActionPermission = this.hasActionPermissions(userPermission, feature, normalActions);

    const inviteColleagueLabelEnabled = await firstValueFrom(this.tenantService.useInviteColleagueLabel());

    const allowSupplierCreateUser =
      feature === 'user' &&
      actions.includes('createSupplier') &&
      userRole === 'supplier' &&
      inviteColleagueLabelEnabled;

    return hasOwnActionPermission || hasActionPermission || allowSupplierCreateUser;
  }

  async hasAccessToOwnData(
    referenceID: string | null,
    userPermission: FeaturePermissionDto[],
    feature: string,
    actions: string[]
  ): Promise<boolean> {
    if (!actions?.length) {
      return false;
    }

    let effectiveRef = referenceID;

    if (!effectiveRef && feature === 'request') {
      const supplier = await this.getUserSupplierV1();
      effectiveRef = supplier?.id ?? '';
    }

    if (!effectiveRef) {
      return false;
    }

    const isUsersTargetData = await this.isUsersTargetData(feature, effectiveRef);
    return isUsersTargetData && this.hasActionPermissions(userPermission, feature, actions);
  }

  hasActionPermissions(userPermission: FeaturePermissionDto[], feature: string, actions: string[]): boolean {
    if (!actions?.length) {
      return false;
    }

    return userPermission.some(
      (permission) => permission.feature === feature && permission.actions?.some((action) => actions.includes(action))
    );
  }

  public async getUserSupplierV1(): Promise<SupplierListV1 | null> {
    return await firstValueFrom(this.store.select(selectUserSupplierV1));
  }

  private async isUsersTargetData(feature: string, referenceId: string): Promise<boolean> {
    if (feature !== 'request') return false;

    const supplier = await this.getUserSupplierV1();
    if (!supplier?.id) return false;
    return referenceId === supplier.id;
  }
}
